import { defineStore } from "pinia"
import { i18n } from "@/i18n"

import DestinationProviderDataService from "@/services/DestinationProviderDataService"
import { useAlertStore } from "./alert"
import { useAccountStore } from "./account"
import { useAuthenticationStore } from "./authentication"
import { AlertType } from "@/static/Alert"
import { DestinationProviderScope, Space } from "@/static/Space"
import type { IDestinationProvider, ITourismInformationCenterOption } from "@/types/DestinationProvider.types"
import {
  decodeDialogueCampaignSource,
  findTicIdForCampaignSource,
} from "@/utils/dialogueCampaignSource"

let tourismInformationCentersLoadPromise: Promise<void> | null = null

export const useSpaceStore = defineStore("space", {
  state: () => ({
    currentSpace: Space.PILOTAGE as Space,

    /** Toutes les fiches de l'utilisateur, tous offices de tourisme confondus. */
    destinationProviders: [] as IDestinationProvider[],

    /** Fiches listées sur l'accueil Dialogue, pour l'office de tourisme sélectionné. */
    dialogueDestinationProviders: [] as IDestinationProvider[],

    /** Fiches sans établissement proposables à la création d'un démonstrateur. */
    pilotableDestinationProviders: [] as IDestinationProvider[],

    tourismInformationCenters: [] as ITourismInformationCenterOption[],

    currentTicId: null as number | null,

    isLoading: false,
  }),

  getters: {
    isPilotage: (state): boolean => state.currentSpace === Space.PILOTAGE,

    isDialogue: (state): boolean => state.currentSpace === Space.DIALOGUE,

    /**
     * Le switch n'apparaît que si le compte dispose d'au moins une fiche,
     * et pas pour les profils commerciaux (SALE / CSM).
     */
    canSwitch: (): boolean => {
      const accountStore = useAccountStore()
      const authenticationStore = useAuthenticationStore()

      if (authenticationStore.isSale) {
        return false
      }

      return accountStore.hasDestinationProviders
    },

    currentTic: (state): ITourismInformationCenterOption | null => {
      if (state.currentTicId == null) {
        return null
      }

      return (
        state.tourismInformationCenters.find(
          (tic) => Number(tic.id) === Number(state.currentTicId)
        ) ?? null
      )
    },

    /**
     * Le sélecteur d'office de tourisme est masqué quand il n'y a rien à sélectionner.
     */
    isMonoTic: (state): boolean => state.tourismInformationCenters.length <= 1,
  },

  actions: {
    clear() {
      this.currentSpace = Space.PILOTAGE
      this.destinationProviders = [] as IDestinationProvider[]
      this.dialogueDestinationProviders = [] as IDestinationProvider[]
      this.pilotableDestinationProviders = [] as IDestinationProvider[]
      this.tourismInformationCenters = [] as ITourismInformationCenterOption[]
      this.currentTicId = null
      this.isLoading = false
      tourismInformationCentersLoadPromise = null
    },

    setSpace(space: Space) {
      this.currentSpace = space
    },

    setCurrentTic(ticId: number | null) {
      this.currentTicId = ticId
    },

    persistDialogueCampaignSource(source: string | null | undefined) {
      if (source) {
        sessionStorage.setItem("requestedDialogueSource", source)
      }
    },

    getPendingDialogueCampaignSource(sourceOverride?: string | null): string | null {
      return sourceOverride ?? sessionStorage.getItem("requestedDialogueSource")
    },

    /**
     * Charge les OT et applique le `source` campagne une fois la liste disponible.
     */
    async ensureTourismInformationCentersReady(sourceOverride?: string | null): Promise<boolean> {
      if (sourceOverride) {
        this.persistDialogueCampaignSource(sourceOverride)
      }

      await this.getTourismInformationCenters()

      return this.getPendingDialogueCampaignSource() == null
    },

    /**
     * Sélectionne l'OT ciblé par le paramètre `source` (account_id du TIC en base64).
     */
    async applyRequestedDialogueSource(sourceOverride?: string | null): Promise<boolean> {
      if (sourceOverride) {
        this.persistDialogueCampaignSource(sourceOverride)
      }

      await this.getTourismInformationCenters()

      return this.getPendingDialogueCampaignSource() == null
    },

    tryApplyCampaignSource(rawSource: string): boolean {
      const accountId = decodeDialogueCampaignSource(rawSource)

      if (!accountId) {
        sessionStorage.removeItem("requestedDialogueSource")
        return false
      }

      const ticId = findTicIdForCampaignSource(this.tourismInformationCenters, accountId)

      if (ticId == null) {
        return false
      }

      this.setCurrentTic(ticId)
      sessionStorage.removeItem("requestedDialogueSource")

      return true
    },

    resolveCurrentTicSelection() {
      const pendingSource = sessionStorage.getItem("requestedDialogueSource")

      if (pendingSource) {
        if (this.tryApplyCampaignSource(pendingSource)) {
          return
        }

        sessionStorage.removeItem("requestedDialogueSource")
      }

      this.ensureValidCurrentTic()
    },

    isKnownTicId(ticId: number | null): boolean {
      if (ticId == null) {
        return false
      }

      return this.tourismInformationCenters.some((tic) => Number(tic.id) === Number(ticId))
    },

    ensureValidCurrentTic() {
      const ids = this.tourismInformationCenters.map((tic) => Number(tic.id))

      if (this.currentTicId != null && ids.includes(Number(this.currentTicId))) {
        return
      }

      this.currentTicId = this.tourismInformationCenters.length > 0 ? this.tourismInformationCenters[0].id : null
    },

    getTourismInformationCenters(callback?: (tics: ITourismInformationCenterOption[]) => void) {
      if (tourismInformationCentersLoadPromise) {
        return tourismInformationCentersLoadPromise.then(() => {
          if (callback) {
            callback(this.tourismInformationCenters)
          }
        })
      }

      tourismInformationCentersLoadPromise = DestinationProviderDataService.getTourismInformationCenters()
        .then(
          (res) => {
            this.tourismInformationCenters = res.data.data.tourism_information_centers

            if (sessionStorage.getItem("requestedDialogueSource")) {
              this.currentTicId = null
            }

            this.resolveCurrentTicSelection()

            if (callback) {
              callback(this.tourismInformationCenters)
            }
          },
          (e) => this._handleError(e)
        )
        .finally(() => {
          tourismInformationCentersLoadPromise = null
        })

      return tourismInformationCentersLoadPromise
    },

    getDestinationProviders(
      scope?: DestinationProviderScope,
      callback?: (destinationProviders: IDestinationProvider[]) => void
    ) {
      this.isLoading = true

      const ticId = scope === DestinationProviderScope.DIALOGUE ? this.currentTicId : null

      return DestinationProviderDataService.getDestinationProviders(scope, ticId).then(
        (res) => {
          const destinationProviders: IDestinationProvider[] = res.data.data.destination_providers

          if (scope === DestinationProviderScope.DIALOGUE) {
            this.dialogueDestinationProviders = destinationProviders
          } else if (scope === DestinationProviderScope.PILOTABLE) {
            this.pilotableDestinationProviders = destinationProviders
          } else {
            this.destinationProviders = destinationProviders
          }

          this.isLoading = false

          if (callback) {
            callback(destinationProviders)
          }
        },
        (e) => {
          this.isLoading = false
          this._handleError(e)
        }
      )
    },

    _handleError(e: any) {
      const alertStore = useAlertStore()

      switch (e.response?.status) {
        case 401:
          break
        default:
          alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
          break
      }
    },
  },

  persist: {
    enabled: true,
    strategies: [
      {
        paths: ["currentSpace", "currentTicId"],
      },
    ],
  },
})
